import useTranslation from "../hooks/translations";
import DefaultLayout from "../layouts/DefaultLayout";
import {Calendar, TrendingDown, TrendingUp} from "react-feather";
import {Button, Card, Container, Spinner, Tab, Tabs} from "react-bootstrap";
import {useDateRange} from "../contexts/DateRangeProvider";
import {forwardRef, useCallback, useEffect, useMemo, useState} from "react";
import LineChart from "../components/LineChart";
import {useApi} from "../contexts/ApiProvider";
import CostService from "../services/CostService";
import IncomeService from "../services/IncomeService";
import CurrencyUtils from "../utils/CurrencyUtils";
import DatesUtils from "../utils/dates";
import useMonths from "../hooks/months";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DatePicker from "react-datepicker";
import useQuarters from "../hooks/quarters";
import StackedBarChart from "../components/StackedBarChart";
import {String2HexCodeColor} from 'string-to-hex-code-color';

import "react-datepicker/dist/react-datepicker.css";
import ProtectedContent from "../components/ProtectedContent";

const defaultChartBackgroundColors = [
  "rgba(0, 97, 242, 1)",
  "rgba(0, 172, 105, 1)",
  "rgba(88, 0, 232, 1)",
  "rgba(244, 161, 0, 1)",
  "rgba(232, 21, 0, 1)",
  "rgba(227, 0, 89, 1)"
];

export default function QuarterlyReportPage() {
  const months = useMonths();
  const quarters = useQuarters();
  const translations = useTranslation();

  const pageTitle = translations.quarterlyReport;
  const pageSubtitle = translations.quarterlyReportSubtitle;

  const {from} = useDateRange();

  const [year, setYear] = useState(from.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(from.getMonth() / 3));

  const [dateFrom, setDateFrom] = useState(new Date(year, quarter * 3, 1));
  const [dateTo, setDateTo] = useState(new Date(year, (quarter + 1) * 3, 0));

  useEffect(() => {
    const from = new Date(year, quarter * 3, 1);
    const to = new Date(new Date(year, (quarter + 1) * 3, 0));

    setDateFrom(from);
    setDateTo(to);

    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostsData(from, to),
        getIncomesData(from, to)
      ]);
      setIsLoading(false);
    })();
  }, [year, quarter]);

  const chartMonths = useMemo(() => {
    const range = [];

    if (!dateFrom || !dateTo) {
      return range;
    }

    const monthFrom = dateFrom.getMonth();
    const monthTo = dateTo.getMonth();

    if (monthFrom > monthTo) {
      return range;
    }

    for (let monthIndex = monthFrom; monthIndex <= monthTo; monthIndex++) {
      range.push(monthIndex);
    }

    return range;
  }, [dateFrom, dateTo]);

  const chartMonthNames = useMemo(() => {
    const names = [];

    if (!dateFrom || !dateTo) {
      return names;
    }

    const monthFrom = dateFrom.getMonth();
    const monthTo = dateTo.getMonth();

    if (monthFrom > monthTo) {
      return names;
    }

    for (let monthIndex = monthFrom; monthIndex <= monthTo; monthIndex++) {
      names.push(months[monthIndex]);
    }

    return names;
  }, [months, dateFrom, dateTo]);

  const api = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const costService = useMemo(() => new CostService(api), [api]);
  const incomeService = useMemo(() => new IncomeService(api), [api]);

  const [costsData, setCostsData] = useState([]);
  const [incomesData, setIncomesData] = useState([]);

  const getCostsData = useCallback(async (start, end) => {
    const response = await costService.search(start, end);
    setCostsData(
      response.body.map((row) => {
        const dateObject = new Date(row.timestamp);
        const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
          row.currency.symbol
        );
        return {
          id: row.id,
          name: row.name,
          type: row.type.name,
          typeId: row.type.id,
          category: row.category.name,
          categoryId: row.category.id,
          amountFormatted: currencyFormatter.format(row.amount),
          amount: row.amount,
          month: dateObject.getMonth(),
          date: DatesUtils.formatDate(dateObject),
          dateFormatted: DatesUtils.format(dateObject),
          currencyId: row.currency.id
        }
      })
    );
  }, [costService]);

  const getIncomesData = useCallback(async (start, end) => {
    const response = await incomeService.search(start, end);
    setIncomesData(
      response.body.map((row) => {
        const dateObject = new Date(row.timestamp);
        const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
          row.currency.symbol
        );
        return {
          id: row.id,
          name: row.name,
          type: row.type.name,
          typeId: row.type.id,
          category: row.category.name,
          categoryId: row.category.id,
          amountFormatted: currencyFormatter.format(row.amount),
          amount: row.amount,
          month: dateObject.getMonth(),
          date: DatesUtils.formatDate(dateObject),
          dateFormatted: DatesUtils.format(dateObject),
          currencyId: row.currency.id
        }
      })
    );
  }, [incomeService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostsData(dateFrom, dateTo),
        getIncomesData(dateFrom, dateTo)
      ]);
      setIsLoading(false);
    })();
  }, [dateFrom, dateTo, getCostsData, getIncomesData]);

  const totalCosts = useMemo(() => {
    return costsData.reduce((accumulatedCosts, currentCost) => {
      return accumulatedCosts + currentCost.amount;
    }, 0)
  }, [costsData]);

  const totalIncomes = useMemo(() => {
    return incomesData.reduce((accumulatedCosts, currentCost) => {
      return accumulatedCosts + currentCost.amount
    }, 0);
  }, [incomesData]);

  const [costsGroupedData, setCostsGroupedData] = useState({});
  const [incomesGroupedData, setIncomesGroupedData] = useState({});

  const getChartData = (months, values, defaultValue = 0) => {
    return months.map(month => values[month] ?? defaultValue);
  };

  useEffect(() => {
    const costsGroupedByMonth = costsData.reduce((groupedCosts, cost) => {
      const {month} = cost;
      groupedCosts[month] = groupedCosts[month] ?? 0;
      groupedCosts[month] += cost.amount;
      return groupedCosts;
    }, {});

    setCostsGroupedData(costsGroupedByMonth);

    setCostsDataset({
      label: translations.costs,
      data: getChartData(chartMonths, costsGroupedByMonth),
      borderColor: "rgba(232, 21, 0, 1)",
      backgroundColor: "rgba(232, 21, 0, 0.1)",
      fill: true,
      tension: 0.25,
      borderWidth: 2
    });

    const types = [...new Set(costsData.map(income => income.type))];
    const costsGroupedByMonthAndType = costsData.reduce((groupedCosts, cost) => {
      const {type, month} = cost;
      groupedCosts[type] = groupedCosts[type] ?? {};
      groupedCosts[type][month] = (groupedCosts[type][month] ?? 0) - cost.amount;
      return groupedCosts;
    }, {});

    const string2HexCodeColor = new String2HexCodeColor();
    setCostsDatasets(
      types.map((type, index) => {
        return {
          label: type,
          data: chartMonths.map((month) => costsGroupedByMonthAndType[type][month] ?? 0),
          backgroundColor: defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(type)
        }
      })
    );
  }, [chartMonths, costsData]);

  useEffect(() => {
    const averageCosts = totalCosts / chartMonths.length;
    setCostsAverageDataset({
      label: translations.averageCosts,
      data: getChartData(chartMonths, [], averageCosts),
      pointStyle: false,
      borderWidth: 1.5,
      borderDash: [10, 5],
      borderColor: "rgba(244, 161, 0, 1)",
      backgroundColor: "rgba(244, 161, 0, 0.1)"
    });
  }, [chartMonths, totalCosts]);

  useEffect(() => {
    const incomesGroupedByMonth = incomesData.reduce((groupedIncomes, income) => {
      const {month} = income;
      groupedIncomes[month] = groupedIncomes[month] ?? 0;
      groupedIncomes[month] += income.amount;
      return groupedIncomes;
    }, {});

    setIncomesGroupedData(incomesGroupedByMonth);

    setIncomesDataset({
      label: translations.incomes,
      data: getChartData(chartMonths, incomesGroupedByMonth),
      borderColor: "rgba(0, 172, 105, 1)",
      backgroundColor: "rgba(0, 172, 105, 0.1)",
      fill: true,
      tension: 0.25,
      borderWidth: 2
    });

    const types = [...new Set(incomesData.map(income => income.type))];
    const incomesGroupedByMonthAndType = incomesData.reduce((groupedIncomes, income) => {
      const {type, month} = income;
      groupedIncomes[type] = groupedIncomes[type] ?? {};
      groupedIncomes[type][month] = (groupedIncomes[type][month] ?? 0) + income.amount;
      return groupedIncomes;
    }, {});

    const string2HexCodeColor = new String2HexCodeColor();
    setIncomesDatasets(
      types.map((type, index) => {
        return {
          label: type,
          data: chartMonths.map((month) => incomesGroupedByMonthAndType[type][month] ?? 0),
          backgroundColor: string2HexCodeColor.stringToColor(type)
          // backgroundColor: defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(type)
        }
      })
    );
  }, [chartMonths, incomesData]);

  useEffect(() => {
    const averageIncomes = totalIncomes / chartMonths.length;
    setAverageIncomesDataset({
      label: translations.averageIncomes,
      data: getChartData(chartMonths, [], averageIncomes),
      pointStyle: false,
      borderWidth: 1.5,
      borderDash: [10, 5],
      borderColor: "rgba(0, 97, 242, 1)",
      backgroundColor: "rgba(0, 97, 242, 0.1)"
    });
  }, [chartMonths, totalIncomes]);

  const [costsDataset, setCostsDataset] = useState({
    data: []
  });

  const [incomesDataset, setIncomesDataset] = useState({
    data: []
  });

  const [costsAverageDataset, setCostsAverageDataset] = useState({
    data: []
  });

  const [incomesAverageDataset, setAverageIncomesDataset] = useState({
    data: []
  });

  const [costsDatasets, setCostsDatasets] = useState([]);
  const [incomesDatasets, setIncomesDatasets] = useState([]);

  const QuarterPickerCustomInput = forwardRef(
    ({value, onClick, className}, ref) => (
      <Button variant="link outline-primary" className={className}
              onClick={onClick} ref={ref}>
        <Calendar/>
      </Button>
    ),
  );

  return (<DefaultLayout icon={<Calendar/>}
                         title={pageTitle}
                         subtitle={pageSubtitle}>
      <ProtectedContent
        allowedRoles={["Admin", "Moderator"]}
        alt={<div style={{textAlign: "center", margin: 10}}>
          <h1>{translations.permissionsAlert}</h1></div>}>
        <Container fluid="xl" className="px-4 mt-n10">
          <Card className="card-header-actions mx-auto mb-4">
            <Card.Header>
              <span className="d-block d-md-none">
                {quarters[quarter]}
              </span>
              <span className="d-none d-md-block">
                {quarters[quarter]} ({months[dateFrom.getMonth()]}, {dateFrom.getFullYear()} - {months[dateTo.getMonth()]}, {dateTo.getFullYear()})
              </span>
              <div>
                <DatePicker
                  selected={dateFrom}
                  showQuarterYearPicker
                  dateFormat="yyyy, QQQ"
                  customInput={<QuarterPickerCustomInput/>}
                  popperPlacement="bottom-end"
                  popperClassName="quarter-datepicker-popper"
                  onChange={(date) => {
                    setYear(Math.floor(date.getFullYear()));
                    setQuarter(Math.floor(date.getMonth() / 3));
                  }}
                />
              </div>
            </Card.Header>
            <Card.Body>
              <Tabs
                id="quarterly-report-tabs"
                defaultActiveKey="quarter-overview"
                className="mb-3"
                justify
              >
                <Tab eventKey="quarter-overview" title={translations.overview}>
                  <div className="row">
                    {chartMonths.map(month => (
                      <div key={month} className="col-xl-3 col-md-6 mb-4">
                        <div
                          className="card border-start-lg border-start-primary h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <div
                                  className="small fw-bold text-primary mb-1">
                                  {months[month]}
                                </div>
                                <div className="h6 text-success">
                                  <TrendingUp/>{' '}
                                  {isLoading ? <Spinner
                                    size="sm"/> : CurrencyUtils.getCurrencyFormatter('RSD').format(incomesGroupedData[month] ?? 0)}
                                </div>
                                <div className="h6 text-danger">
                                  <TrendingDown/>{' '}
                                  {isLoading ? <Spinner
                                    size="sm"/> : CurrencyUtils.getCurrencyFormatter('RSD').format(costsGroupedData[month] ?? 0)}
                                </div>
                              </div>
                              <div className="ms-2">
                                <FontAwesomeIcon icon="dollar-sign"
                                                 className="fa-2x text-gray-200"/>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div key="average" className="col-xl-3 col-md-6 mb-4">
                      <div
                        className="card border-start-lg border-start-primary h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <div
                                className="small fw-bold text-primary mb-1">
                                {translations.average} ({quarters[quarter]})
                              </div>
                              <div className="h6 text-primary">
                                <TrendingUp/>{' '}
                                {isLoading ? <Spinner
                                  size="sm"/> : CurrencyUtils.getCurrencyFormatter('RSD').format(totalIncomes / chartMonths.length)}
                              </div>
                              <div className="h6 text-warning">
                                <TrendingDown/>{' '}
                                {isLoading ? <Spinner
                                  size="sm"/> : CurrencyUtils.getCurrencyFormatter('RSD').format(totalCosts / chartMonths.length)}
                              </div>
                            </div>
                            <div className="ms-2">
                              <FontAwesomeIcon icon="dollar-sign"
                                               className="fa-2x text-gray-200"/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    {isLoading ? <Spinner/> : <LineChart chartData={{
                      labels: chartMonthNames,
                      datasets: [
                        costsDataset,
                        incomesDataset,
                        costsAverageDataset,
                        incomesAverageDataset
                      ]
                    }} aspectRatio={1.25}/>}
                  </div>
                </Tab>
                <Tab eventKey="quarter-details" title={translations.inDetail}>
                  {isLoading ? <Spinner/> : <StackedBarChart chartData={{
                    labels: chartMonthNames,
                    datasets: [
                      ...costsDatasets,
                      ...incomesDatasets
                    ]
                  }} aspectRatio={1}/>}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Container>
      </ProtectedContent>
    </DefaultLayout>
  );
}
